/**
 * WebSocket-backed `SnapshotSource` for the WP6 renderer.
 *
 * Contract: WP6's `mountRenderer` takes a `SnapshotSource` with
 * `subscribe(cb) → { dispose }`. WP7's job is to route WS `snapshot` and `delta`
 * messages into that callback (converting the `WireSimState` + tick
 * into WP6's `SnapshotEntry` shape).
 */

import type { SnapshotEntry } from "./snapshots.js";
import { applyWireDelta } from "./snapshots.js";
import type { SnapshotSource, SnapshotSourceHandle } from "../render/index.js";
import type { ServerMessage, WireSimState } from "@pit/protocol";

/**
 * Pushable, subscribable snapshot channel. WP6 sees the read half;
 * the WS-message handler writes via `push`.
 */
export class WsSnapshotSource implements SnapshotSource {
  private readonly subscribers = new Set<(e: SnapshotEntry) => void>();
  private buffered: SnapshotEntry[] = [];
  private lastEntry: SnapshotEntry | null = null;
  /** Authoritative accumulated state (#1055) for delta reconstruction. */
  private canonicalState: WireSimState | null = null;

  /** Reset canonical state (e.g. on new match or full baseline snapshot). */
  reset(): void {
    this.canonicalState = null;
    this.lastEntry = null;
    this.buffered = [];
  }

  /**
   * Push a snapshot from the socket layer. If nobody's subscribed
   * yet, buffer up to `RING_CAPACITY` frames; the buffer is drained
   * on subscribe. Also cached separately for HUD sampler polling.
   */
  push(entry: SnapshotEntry): void {
    this.canonicalState = entry.state;
    this.lastEntry = entry;
    if (this.subscribers.size > 0) {
      for (const s of this.subscribers) s(entry);
      return;
    }
    this.buffered.push(entry);
    if (this.buffered.length > 64) {
      this.buffered.shift();
    }
  }

  /**
   * Apply a delta message onto current canonical state and push the
   * reconstructed full snapshot (#1055).
   */
  pushDelta(msg: Extract<ServerMessage, { type: "delta" }>): boolean {
    if (this.canonicalState === null) {
      // Delta received before initial snapshot — dropped
      return false;
    }
    const reconstructed = applyWireDelta(this.canonicalState, msg);
    this.push({ tick: msg.tick, state: reconstructed });
    return true;
  }

  /**
   * WP6-facing subscribe. Fan-out: every live subscriber receives every
   * push. A new subscriber first drains any pre-subscribe buffer so a
   * late-mounting renderer catches up on the first frame. Dispose is
   * scoped to the exact callback registered, so a torn-down renderer
   * can never detach a still-live one.
   */
  subscribe(onSnapshot: (entry: SnapshotEntry) => void): SnapshotSourceHandle {
    this.subscribers.add(onSnapshot);
    const pending = this.buffered;
    this.buffered = [];
    for (const e of pending) onSnapshot(e);
    return {
      dispose: (): void => {
        this.subscribers.delete(onSnapshot);
      },
    };
  }

  /** For tests. */
  peekBuffered(): readonly SnapshotEntry[] {
    return this.buffered.slice();
  }

  /**
   * Latest WireSimState pushed through the source (or null). Used by
   * the HUD sampler to read snapshots into React state at 10 Hz
   * without competing with WP6's ring buffer subscriber slot.
   */
  latestSnapshot(): WireSimState | null {
    return this.lastEntry === null ? null : this.lastEntry.state;
  }
}

/**
 * Handle a parsed `snapshot` or `delta` server message: adapt to a
 * `SnapshotEntry` and push. Returns true if the message was handled.
 */
export function handleSnapshotMessage(
  source: WsSnapshotSource,
  msg: ServerMessage,
): boolean {
  if (msg.type === "snapshot") {
    source.push({ tick: msg.tick, state: msg.state });
    return true;
  }
  if (msg.type === "delta") {
    return source.pushDelta(msg);
  }
  return false;
}
